"use client"

import { Suspense, useState, useEffect } from "react"
import { useLoader } from "@react-three/fiber"
import * as THREE from "three"
import type { MinecraftColorProperties, MinecraftModel } from "@/types"
import { resolveModelInheritance } from "@/lib/model-resolver"
import { Html } from "@react-three/drei"
import { prepareIdForPath, SceneDescription, SingleModelDisplay } from "./models"
import { Skeleton } from "../ui/skeleton"

function useTextureMap(paths: { [key: string]: string }): { [key: string]: THREE.Texture } {
  const urls = Object.values(paths)
  const keys = Object.keys(paths)

  // useLoader can accept an array of URLs
  const loadedTextures = useLoader(THREE.TextureLoader, urls)

  const textureMap: { [key: string]: THREE.Texture } = {}
  keys.forEach((key, index) => {
    const texture = loadedTextures[index]
    if (texture) {
      texture.magFilter = THREE.NearestFilter
      texture.minFilter = THREE.NearestFilter
      textureMap[key] = texture
    }
  })
  return textureMap
}

function useAnimatedTextureMap(paths: { [key: string]: string }): { [key: string]: THREE.Texture } {
  const urls = Object.values(paths)
  const keys = Object.keys(paths)

  const textureMap: { [key: string]: THREE.Texture } = {}
  urls.forEach((url, index) => {
    const texture = new THREE.TextureLoader().load(url)
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    textureMap[keys[index]] = texture
  })
  return textureMap
}

function ModelRenderer({ id, colorProps }: { id: string; colorProps?: MinecraftColorProperties }) {
  const [modelSceneCache, setModelSceneCache] = useState<Map<string, SceneDescription>>(new Map())
  useEffect(() => {
    const f = async () => {
      const modelProcessingPromises: Promise<void>[] = []
      const vanillaModelsCache = new Map<string, MinecraftModel | "not_found" | "fetching">()

      const idForSceneCache = prepareIdForPath(id)
      const newModelSceneCache = new Map(modelSceneCache) // Preserve existing successfully fetched scenes

      modelProcessingPromises.push(
        resolveModelInheritance(id, new Map(), vanillaModelsCache)
          .then(async (resolvedModelJson) => {
            if (resolvedModelJson && resolvedModelJson.elements && resolvedModelJson.elements.length > 0) {
              return await fetch("/api/minecraft-to-threejs", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ modelJson: resolvedModelJson }),
              })
                .then(async (response) => {
                  if (!response.ok) {
                    const errData = await response.json()
                    throw new Error(
                      `API for '${id}' failed: ${errData.error || response.status}`,
                    )
                  }
                  return response.json() as Promise<SceneDescription>
                })
                .then((sceneDesc) => {
                  newModelSceneCache.set(idForSceneCache, sceneDesc)
                })
                
              } else if (resolvedModelJson) {
                // Resolved but no elements (e.g. empty model)
                newModelSceneCache.set(idForSceneCache, { elements: [] })
              } else {
                // Could not resolve model
                console.warn(
                  `Model '${id}' could not be resolved. Caching empty scene for ${idForSceneCache}.`,
                )
                newModelSceneCache.set(idForSceneCache, { elements: [] }) // Cache empty to show placeholder
              }
            })
          .catch((err) => {
            console.error(`Error processing model ${id}:`, err)
            // Optionally set an error state or skip this model
              
            // Optionally set an error for this specific model or cache an error state
            newModelSceneCache.set(idForSceneCache, { elements: [] }) // Cache empty on error
          }),
      )

      try {
        await Promise.all(modelProcessingPromises)
        setModelSceneCache(newModelSceneCache)
      } catch(error: any) {
        console.error(error)
      }
    }
    f()
  }, [])

  return (
    <group
      key={id}
      position={[0, 0, 0]}
    >
      <SingleModelDisplay sceneDescription={modelSceneCache.get(id)!} colorProps={colorProps} />
    </group>
  )
}

export default function ItemModel({ itemId, colorProps }: { itemId: string; colorProps?: MinecraftColorProperties }) {
  return (
    <Suspense
      fallback={
        <Html center>
          <Skeleton className="w-4 h-4"></Skeleton>
        </Html>
      }
    >
      <ModelRenderer id={itemId} colorProps={colorProps} />
    </Suspense>
  )
}
